import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from '../../i18n/useTranslation';
import { Alert } from '../atoms/Alert/Alert';
import { Button } from '../atoms/Button/Button';
import { Inline } from '../atoms/Inline/Inline';
import { Input } from '../atoms/Input/Input';
import { Radio } from '../atoms/Radio/Radio';
import { Select } from '../atoms/Select/Select';
import { Stack } from '../atoms/Stack/Stack';
import { Text } from '../atoms/Typography/Text';
import { LIGHT_GROUPS, ZONES, type CoralInput, type LightGroup, type PointInput, type Zone } from '../../services/lightSurveyService';
import './Lighting.css';

export type EditorMode = 'point' | 'coral';

export interface CoralOption {
  readonly id: string;
  readonly name: string;
}

/** What is being edited — null: adding a new one. */
export type EditTarget =
  | { kind: 'point'; id: string; x: number; y: number; par: number; zone: Zone | null }
  | { kind: 'coral'; id: string; x: number; y: number; zone: Zone | null; livestockId: string | null; label: string | null; lightGroup: LightGroup | null };

export interface PlacementEditorProps {
  mode: EditorMode;
  onModeChange: (mode: EditorMode) => void;
  /** Position picked on the photo (0–1); the fields can also be typed. */
  position: { x: number; y: number } | null;
  onPositionChange: (position: { x: number; y: number } | null) => void;
  editing: EditTarget | null;
  onCancelEdit: () => void;
  corals: readonly CoralOption[];
  pending: boolean;
  failed: boolean;
  onSavePoint: (input: Required<Pick<PointInput, 'x' | 'y' | 'par'>> & PointInput, id: string | null) => void;
  onSaveCoral: (input: Required<Pick<CoralInput, 'x' | 'y'>> & CoralInput, id: string | null) => void;
}

const toPercent = (v: number | undefined): string => (v === undefined ? '' : String(Math.round(v * 1000) / 10));

function parsePercent(value: string): number | null {
  if (value.trim() === '') return null;
  const n = Number(value.replace(',', '.'));
  return Number.isFinite(n) && n >= 0 && n <= 100 ? n / 100 : null;
}

/**
 * The form beside the photo: add or edit a measured point (PAR + height zone) or
 * a coral (from the livestock or by name, optional care group). The position is
 * a pair of percentage fields — tapping the photo fills them, keyboard and
 * screen-reader users type them.
 */
export function PlacementEditor({
  mode,
  onModeChange,
  position,
  onPositionChange,
  editing,
  onCancelEdit,
  corals,
  pending,
  failed,
  onSavePoint,
  onSaveCoral,
}: PlacementEditorProps) {
  const { t } = useTranslation();
  const [x, setX] = useState(toPercent(position?.x));
  const [y, setY] = useState(toPercent(position?.y));
  const [par, setPar] = useState('');
  const [zone, setZone] = useState<Zone | ''>('');
  const [livestockId, setLivestockId] = useState('');
  const [label, setLabel] = useState('');
  const [group, setGroup] = useState<LightGroup | ''>('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  // A tap on the photo moves the position fields.
  useEffect(() => {
    setX(toPercent(position?.x));
    setY(toPercent(position?.y));
  }, [position?.x, position?.y]);

  // Editing loads the item into the form.
  useEffect(() => {
    setErrors({});
    if (!editing) return;
    setZone(editing.zone ?? '');
    if (editing.kind === 'point') {
      setPar(String(editing.par));
    } else {
      setLivestockId(editing.livestockId ?? '');
      setLabel(editing.label ?? '');
      setGroup(editing.lightGroup ?? '');
    }
  }, [editing]);

  const reset = (): void => {
    setPar('');
    setLabel('');
    setLivestockId('');
    setGroup('');
    setErrors({});
    onPositionChange(null);
  };

  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const next: Record<string, string> = {};
    const px = parsePercent(x);
    const py = parsePercent(y);
    if (px === null) next.x = String(t('lighting.editor.positionInvalid'));
    if (py === null) next.y = String(t('lighting.editor.positionInvalid'));
    if (mode === 'point') {
      const value = Number(par.replace(',', '.'));
      if (par.trim() === '' || !Number.isFinite(value) || value < 0 || value > 3000) next.par = String(t('lighting.editor.parInvalid'));
      setErrors(next);
      if (Object.keys(next).length > 0) return;
      onSavePoint({ x: px!, y: py!, par: Math.round(value * 10) / 10, zone: zone || null }, editing?.kind === 'point' ? editing.id : null);
    } else {
      if (!livestockId && label.trim() === '') next.coral = String(t('lighting.editor.coralRequired'));
      setErrors(next);
      if (Object.keys(next).length > 0) return;
      onSaveCoral(
        { x: px!, y: py!, zone: zone || null, livestock_id: livestockId || null, label: livestockId ? null : label.trim(), light_group: group || null },
        editing?.kind === 'coral' ? editing.id : null,
      );
    }
  };

  const isEdit = editing !== null;
  return (
    <form onSubmit={submit} noValidate data-testid="placement-editor">
      <Stack gap="4">
        {!isEdit && (
          <fieldset className="lighting__fieldset">
            <legend className="lighting__legend-title">{t('lighting.editor.modeLegend')}</legend>
            <Radio name="light-editor-mode" label={t('lighting.editor.modePoint')} checked={mode === 'point'} onChange={() => onModeChange('point')} />
            <Radio name="light-editor-mode" label={t('lighting.editor.modeCoral')} checked={mode === 'coral'} onChange={() => onModeChange('coral')} />
          </fieldset>
        )}
        <Text color="secondary" size="sm">{t(mode === 'point' ? 'lighting.editor.hintPoint' : 'lighting.editor.hintCoral')}</Text>
        <Inline gap="3" align="start">
          <Input id="light-pos-x" type="number" inputMode="decimal" min={0} max={100} step={0.1} label={t('lighting.editor.x')} value={x} onChange={(e) => setX(e.target.value)} error={errors.x} />
          <Input id="light-pos-y" type="number" inputMode="decimal" min={0} max={100} step={0.1} label={t('lighting.editor.y')} value={y} onChange={(e) => setY(e.target.value)} error={errors.y} />
        </Inline>
        {mode === 'point' ? (
          <Input id="light-par" type="number" inputMode="decimal" min={0} max={3000} label={t('lighting.editor.par')} value={par} onChange={(e) => setPar(e.target.value)} error={errors.par} />
        ) : (
          <Stack gap="3">
            <Select
              id="light-coral-livestock"
              label={t('lighting.editor.coralFromLivestock')}
              value={livestockId}
              options={[{ value: '', label: String(t('lighting.editor.coralByName')) }, ...corals.map((c) => ({ value: c.id, label: c.name }))]}
              onChange={(e) => setLivestockId(e.target.value)}
            />
            {!livestockId && (
              <Input id="light-coral-name" label={t('lighting.editor.coralName')} value={label} onChange={(e) => setLabel(e.target.value)} error={errors.coral} maxLength={120} placeholder={t('lighting.editor.coralNamePlaceholder')} />
            )}
            <Select
              id="light-coral-group"
              label={t('lighting.editor.group')}
              value={group}
              options={[{ value: '', label: String(t('lighting.groups.auto')) }, ...LIGHT_GROUPS.map((g) => ({ value: g, label: String(t(`lighting.groups.${g}`)) }))]}
              onChange={(e) => setGroup(e.target.value as LightGroup | '')}
            />
          </Stack>
        )}
        <Select
          id="light-zone"
          label={t('lighting.editor.zone')}
          value={zone}
          options={[{ value: '', label: String(t('lighting.zones.unknown')) }, ...ZONES.map((z) => ({ value: z, label: String(t(`lighting.zones.${z}`)) }))]}
          onChange={(e) => setZone(e.target.value as Zone | '')}
        />
        {failed && <Alert compact>{t('lighting.editor.error')}</Alert>}
        <Inline gap="2" wrap>
          <Button type="submit" variant="secondary" isLoading={pending}>
            {t(isEdit ? 'lighting.editor.save' : mode === 'point' ? 'lighting.editor.addPoint' : 'lighting.editor.addCoral')}
          </Button>
          {isEdit && (
            <Button type="button" variant="ghost" onClick={() => { reset(); onCancelEdit(); }}>
              {t('lighting.cancel')}
            </Button>
          )}
        </Inline>
      </Stack>
    </form>
  );
}

export default PlacementEditor;
